<template>
  <div class="page-container task-list-page">
    <div class="card">
      <!-- 顶部：面包屑 + 操作按钮 -->
      <div class="card-header">
        <div class="header-left">
          <el-button type="text" icon="el-icon-arrow-left" @click="backToTaskIndex">返回</el-button>
          <el-divider direction="vertical" />
          <span class="breadcrumb">任务管理</span>
          <i class="el-icon-arrow-right breadcrumb-sep" />
          <span class="breadcrumb current">{{ groupName }}</span>
        </div>
        <div class="header-right">
          <!-- 仅日常周期：控制 0 点定时是否自动创建今日子任务（「补建今日任务」兜底仍可用） -->
          <div v-if="cycleType === 'day'" class="day-auto-wrap">
            <span class="day-auto-label">定时自动创建</span>
            <el-tooltip placement="top">
              <div slot="content" style="max-width: 360px; line-height: 1.5">
                系统每日 0 点按本组内「最近一天」期次任务的配置（设施类型、区域范围、分配方式等）自动生成本日任务。关闭后不再定时创建，仍可通过「补建今日任务」手动补建。
              </div>
              <i class="el-icon-question day-auto-tip-icon" />
            </el-tooltip>
            <el-switch
              v-model="dayAutoCreateOn"
              :disabled="dayAutoCreateLoading || dayAutoCreateSaving"
              active-text="开"
              inactive-text="关"
              @change="onDayAutoCreateChange"
            />
          </div>
          <!-- 日常任务组：定时自动创建失败兜底，仅当今日任务尚不存在时显示 -->
          <el-button
            v-if="cycleType === 'day' && !todayTaskExists"
            type="warning"
            size="small"
            icon="el-icon-refresh"
            :loading="creatingToday"
            @click="handleCreateToday"
          >
            补建今日任务
          </el-button>
          <el-button
            type="success"
            size="small"
            icon="el-icon-plus"
            @click="handleAddTask"
          >
            新增任务
          </el-button>
        </div>
      </div>

      <!-- 筛选条 -->
      <div class="search-bar">
        <el-input
          v-model="queryForm.keyword"
          placeholder="任务名称"
          clearable
          style="width: 200px"
          @keyup.enter.native="handleQuery"
        />
        <el-select
          v-model="queryForm.systemId"
          placeholder="消防系统"
          clearable
          style="width: 160px"
          @change="handleQuery"
        >
          <el-option
            v-for="item in systemList"
            :key="item.uuid"
            :label="item.systemName"
            :value="item.uuid"
          />
        </el-select>

        <!-- 每日任务：期次即自然日，与后端 period_name（yyyy-MM-dd）精确匹配 -->
        <el-date-picker
          v-if="cycleType === 'day'"
          v-model="queryForm.periodName"
          type="date"
          placeholder="任务期次"
          value-format="yyyy-MM-dd"
          clearable
          size="small"
          style="width: 170px"
          @change="handleQuery"
        />

        <!-- 非日常、专项周期：提供对应的周期名称下拉过滤 -->
        <el-select
          v-if="cycleType !== 'day' && cycleType !== 'special'"
          v-model="queryForm.periodName"
          placeholder="任务期次"
          clearable
          style="width: 160px"
          @change="handleQuery"
        >
          <el-option
            v-for="item in periodNameOptions"
            :key="item"
            :label="item"
            :value="item"
          />
        </el-select>
        <el-select
          v-model="queryForm.status"
          placeholder="状态"
          clearable
          style="width: 120px"
          @change="handleQuery"
        >
          <el-option
            v-for="opt in statusSelectOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" @click="handleQuery">搜索</el-button>
        <el-button size="small" icon="el-icon-refresh" @click="handleReset">重置</el-button>

        <!-- 只看未完成开关，每日任务组默认开启 -->
        <div class="filter-switch">
          <el-switch v-model="onlyUnfinished" active-text="只看未完成" @change="onOnlyUnfinishedChange"/>
        </div>
      </div>

      <!-- 子任务表格 -->
      <div class="table-wrapper">
        <el-table
          v-loading="loading"
          :data="tableData"
          border
          size="small"
          style="width: 100%"
          height="100%"
        >
          <el-table-column prop="taskName" label="任务名称" min-width="120" show-overflow-tooltip align="center"/>
          <el-table-column prop="periodName" label="任务期次" width="100" align="center"/>
          <el-table-column prop="systemName" label="消防系统" width="150" align="center"/>
          <el-table-column label="设施类型" width="200" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ getDeviceTypeNames(scope.row.deviceTypes) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="areaNames" label="执行区域" min-width="150" show-overflow-tooltip>
            <template slot-scope="scope">
              <span>{{ scope.row.areaNames || '全大区' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="进度" width="200">
            <template slot-scope="scope">
              <div class="progress-cell">
                <el-progress
                  :percentage="scope.row.progressPercent || 0"
                  :status="scope.row.progressPercent === 100 ? 'success' : null"
                />
                <span class="progress-text">{{ scope.row.completedDevices || 0 }}/{{ scope.row.totalDevices || 0 }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="80" align="center">
            <template slot-scope="scope">
              <el-tag :type="getStatusTag(scope.row.status)" size="mini">
                {{ scope.row.statusName != null && scope.row.statusName !== '' ? scope.row.statusName : scope.row.status }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="分配信息" min-width="130" show-overflow-tooltip align="center">
            <template slot-scope="scope">
              <span v-if="!scope.row.assignType || scope.row.assignType === 'none'" class="text-muted">不指定</span>
              <template v-else-if="scope.row.assignType === 'team'">
                <el-tag type="warning" size="mini" effect="plain">
                  {{ scope.row.assignTeamName || '指定小组' }}
                </el-tag>
                <div v-if="scope.row.teamAssigneeName" class="assign-delegate-text">
                  组内委派：{{ scope.row.teamAssigneeName }}
                </div>
              </template>
              <span v-else>{{ scope.row.assignUserNames || scope.row.assignUserIds || '-' }}</span>
            </template>
          </el-table-column>
          <el-table-column label="异常情况" width="100" align="center">
            <template slot-scope="scope">
              <el-tag v-if="getAbnormalDeviceCount(scope.row) > 0" type="danger" size="mini">
                {{ getAbnormalDeviceCount(scope.row) }}条异常
              </el-tag>
              <span v-else style="color:#c0c4cc">-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right" align="center">
            <template slot-scope="scope">
              <el-button
                type="primary"
                plain
                size="mini"
                @click="handleViewDetail(scope.row)"
              >
                查看详情
              </el-button>
              <el-button
                v-if="scope.row.status === 0"
                type="warning"
                plain
                size="mini"
                @click="handleEditTask(scope.row)"
              >
                编辑
              </el-button>
              <!-- 改派按钮：待执行和进行中均可改派 -->
              <el-button
                v-if="scope.row.status === 0 || scope.row.status === 1"
                type="info"
                plain
                size="mini"
                @click="handleReassign(scope.row)"
              >
                改派
              </el-button>
              <el-button
                v-if="canDeleteTask(scope.row)"
                type="danger"
                plain
                size="mini"
                @click="handleDeleteTask(scope.row)"
              >
                删除
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        :current-page="pagination.pageNo"
        :page-sizes="[10, 20, 50]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 新增/编辑任务弹窗 -->
    <TaskDialog
      :visible.sync="dialogVisible"
      :task-data="currentTask"
      :group-name="groupName"
      :group-id="groupId"
      :cycle-type="cycleType"
      :task-type="taskType"
      :task-year="taskYear"
      :zone-area-id="zoneAreaId"
      :is-split-monthly="isSplitMonthly"
      :system-list="systemList"
      :type-list="typeList"
      :area-tree="areaTree"
      @refresh="refreshList"
    />

    <!-- 查看任务明细弹窗 -->
    <TaskDetailDialog
      :visible.sync="detailVisible"
      :task-data="currentDetailTask"
      :zone-area-id="zoneAreaId"
      :area-tree="areaTree"
    />

    <!-- 改派弹窗 -->
    <el-dialog
      title="改派执行人员"
      :visible.sync="reassignVisible"
      width="680px"
      :close-on-click-modal="false"
      append-to-body
    >
      <el-form :model="reassignForm" label-width="90px">
        <div v-if="reassignTask" class="task-info-bar">
          <span class="label">任务期次：</span>
          <span class="value">{{ reassignTask.periodName }}</span>
          <span class="label" style="margin-left:25px">提示：</span>
          <span class="value" style="color:#e6a23c">将对本期次所有任务统一生效</span>
        </div>
        <AssignTypeFields
          :model="reassignForm"
          team-type="task"
          preset="task"
          label-width="90px"
          :active="reassignVisible"
          @navigate-to-team="handleReassignNavigateTeam"
          @navigate-to-org-user="handleReassignNavigateOrgUser"
        />
      </el-form>
      <div slot="footer">
        <el-button @click="reassignVisible = false">取消</el-button>
        <el-button type="primary" :loading="reassigning" @click="submitReassign">确认改派</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
// 服务
import { taskService } from './task.service';
import { areaService } from '@/features/gnmk/area/area.service';
import { deviceTypeService } from '@/features/gnmk/deviceType/deviceType.service';
import { isTaskManager } from '@/features/common/utils/gnmkRoles';
// 组件
import TaskDialog from './taskDialog.vue';
import TaskDetailDialog from './taskDetailDialog.vue';
import AssignTypeFields from '@/features/gnmk/components/AssignTypeFields.vue';

export default {
  name: 'TaskList',
  components: { TaskDialog, TaskDetailDialog, AssignTypeFields },
  data () {
    return {
      // 路由参数
      groupId: null,
      groupName: '',
      cycleType: null,
      taskType: null,
      taskYear: null,
      zoneAreaId: null,
      /** 任务组是否按月分解（Y/N），与创建任务组 isSplitMonthly 一致 */
      isSplitMonthly: 'N',

      queryForm: { keyword: null, systemId: null, status: null, periodName: null },
      // 每日任务组默认开启只看未完成
      onlyUnfinished: false,

      tableData: [],
      loading: false,
      pagination: { pageNo: 1, pageSize: 20, total: 0 },

      systemList: [],
      typeList: [],
      areaTree: [],

      dialogVisible: false,
      currentTask: null,
      detailVisible: false,
      currentDetailTask: null,
      creatingToday: false,
      todayTaskExists: true,

      /** 日常任务组：是否开启每日 0 点定时自动创建（对应任务组 dayAutoCreate=Y） */
      dayAutoCreateOn: true,
      dayAutoCreateLoading: false,
      dayAutoCreateSaving: false,

      // 改派弹窗相关
      reassignVisible: false,
      reassignTask: null,
      reassignForm: { assignType: 'none', assignUserIds: [], assignTeamId: null },
      reassigning: false
    };
  },
  computed: {
    /**
     * 列表筛选条件中的期次：始终覆盖**整年**（便于查历史/任意月份），
     * 与「新建任务」弹窗 `taskDialog` 中期次裁剪逻辑分开。
     */
    periodNameOptions () {
      const year = Number(this.taskYear) || new Date().getFullYear();
      const type = this.cycleType;
      const split =
        (type === 'year' || type === 'yearly_daily') &&
        (this.isSplitMonthly === 'Y' || this.isSplitMonthly === true);
      if (type === 'month') {
        return Array.from({ length: 12 }, (_, i) => `${year}年${i + 1}月`);
      }
      if (type === 'quarter') {
        return [1, 2, 3, 4].map(q => `${year}年第${q}季度`);
      }
      if (type === 'half_year') {
        return [`${year}年上半年`, `${year}年下半年`];
      }
      if (type === 'year' || type === 'yearly_daily') {
        if (split) {
          return Array.from({ length: 12 }, (_, i) => `${year}年${i + 1}月`);
        }
        return [`${year}年`];
      }
      return [];
    },
    // 「只看未完成」开启时隐藏「已完成」，与 statusList [0,1] 后端条件一致
    statusSelectOptions () {
      const all = [
        { label: '待执行', value: 0 },
        { label: '进行中', value: 1 },
        { label: '已完成', value: 2 }
      ];
      if (this.onlyUnfinished) {
        return all.filter(o => o.value !== 2);
      }
      return all;
    }
  },
  methods: {
    /** 固定回到任务管理列表页（index） */
    backToTaskIndex () {
      this.$router.replace({ name: 'taskList' });
    },

    /** 组装子任务分页参数：「只看未完成」且无单选状态时传 statusList 0、1 */
    buildTaskPageQueryParams () {
      const { status, ...qf } = this.queryForm;
      const params = {
        ...qf,
        groupId: this.groupId,
        pageNo: this.pagination.pageNo,
        pageSize: this.pagination.pageSize
      };
      if (this.onlyUnfinished) {
        if (status === 0 || status === 1) {
          params.status = status;
        } else {
          params.statusList = [0, 1];
        }
      } else if (status === 0 || status === 1 || status === 2) {
        params.status = status;
      }
      return params;
    },

    loadData () {
      if (!this.groupId) return;
      this.loading = true;
      taskService.queryTaskPage(this.buildTaskPageQueryParams()).then(res => {
        const data = res.data;
        this.tableData = data.list || data.rows || [];
        this.pagination.total = data.total || 0;
      }).finally(() => {
        this.loading = false;
      });
    },

    /** 列表变更后刷新：日常任务组同步重检今日期次是否已存在（控制补建按钮显隐） */
    refreshList () {
      this.loadData();
      if (this.cycleType === 'day') {
        this.checkTodayTask();
      }
    },

    loadBaseData () {
      deviceTypeService.listFireSystems().then(res => (this.systemList = res.data || []));
      deviceTypeService.listDeviceTypes().then(res => (this.typeList = res.data || []));
      areaService.getAreaTree().then(res => (this.areaTree = res.data || []));
    },

    handleQuery () { this.pagination.pageNo = 1; this.loadData(); },

    /** 开启「只看未完成」时若当前筛的是已完成则清空，避免与单状态 status、多状态 statusList 同时约束导致无数据 */
    onOnlyUnfinishedChange (val) {
      if (val && Number(this.queryForm.status) === 2) {
        this.queryForm.status = null;
      }
      this.handleQuery();
    },

    handleReset () {
      this.queryForm = { keyword: null, systemId: null, status: null, periodName: null };
      this.onlyUnfinished = this.cycleType === 'day';
      this.handleQuery();
    },

    /** 加载每日 0 点定时自动创建设置 */
    loadDayAutoSetting () {
      if (this.cycleType !== 'day' || !this.groupId) return;
      this.dayAutoCreateLoading = true;
      taskService.getTaskGroup(this.groupId).then(res => {
        const row = res.data || {};
        this.dayAutoCreateOn = row.dayAutoCreate !== 'N';
      }).catch(() => {
        this.dayAutoCreateOn = true;
      }).finally(() => {
        this.dayAutoCreateLoading = false;
      });
    },

    /** 开启/关闭每日 0 点定时自动创建 */
    onDayAutoCreateChange (val) {
      const dayAutoCreate = val ? 'Y' : 'N';
      this.dayAutoCreateSaving = true;
      taskService.updateTaskGroup({ uuid: this.groupId, dayAutoCreate }).then(() => {
        this.$message.success('已保存');
      }).catch(() => {
        this.dayAutoCreateOn = !val;
      }).finally(() => {
        this.dayAutoCreateSaving = false;
      });
    },

    handleSizeChange (val) { this.pagination.pageSize = val; this.loadData(); },
    handleCurrentChange (val) { this.pagination.pageNo = val; this.loadData(); },

    handleViewDetail (task) {
      this.currentDetailTask = task;
      this.detailVisible = true;
    },

    handleAddTask () {
      this.currentTask = null;
      this.dialogVisible = true;
    },

    handleEditTask (task) {
      this.currentTask = task;
      this.dialogVisible = true;
    },

    canDeleteTask (task) {
      if (!task) return false;
      return task.status === 0 || (isTaskManager() && (task.status === 1 || task.status === 2));
    },

    handleDeleteTask (task) {
      const forceDelete = task.status !== 0;
      const message = forceDelete
        ? `当前账号为任务管理员，本次将强制删除任务"${task.taskName}"。该任务为执行中或已完成状态，删除后不可恢复，是否继续？`
        : `确认删除任务"${task.taskName}"吗？`;
      this.$confirm(message, forceDelete ? '强制删除确认' : '提示', {
        type: 'warning',
        confirmButtonText: forceDelete ? '强制删除' : '确定',
        confirmButtonClass: forceDelete ? 'el-button--danger' : ''
      }).then(() => {
        this.loading = true;
        taskService.deleteTask(task.uuid).then(() => {
          this.$message.success('删除成功');
          this.refreshList();
        }).finally(() => {
          this.loading = false;
        });
      }).catch(() => {});
    },

    /** 检查今日任务是否已存在（单独请求，不受只看未完成过滤影响） */
    checkTodayTask () {
      const today = new Date().toISOString().slice(0, 10);
      taskService.queryTaskPage({
        groupId: this.groupId,
        periodName: today,
        pageNo: 1,
        pageSize: 1
      }).then(res => {
        const data = res.data;
        this.todayTaskExists = (data.total || 0) > 0;
      });
    },

    /** 手动补建今日日常任务（定时自动创建失败兜底） */
    handleCreateToday () {
      this.$confirm('将使用最近一天期次任务的配置进行创建，是否继续？', '补建确认', {
        confirmButtonText: '确认补建',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.creatingToday = true;
        taskService.createTodayTasks(this.groupId).then(() => {
          this.$message.success('今日任务创建成功');
          this.refreshList();
        }).finally(() => {
          this.creatingToday = false;
        });
      }).catch(() => {});
    },

    /** 将逗号分隔的设施类型ID转为名称显示 */
    getDeviceTypeNames (deviceTypes) {
      if (!deviceTypes) return '全部类型';
      const ids = deviceTypes.split(',').filter(Boolean);
      if (!ids.length) return '全部类型';
      const names = ids.map(id => {
        const t = this.typeList.find(t => t.uuid === id);
        return t ? t.typeName : id;
      });
      return names.join('、');
    },

    getStatusTag (status) { return { 0: 'info', 1: '', 2: 'success' }[status] ?? ''; },

    /** 与任务详情弹窗一致：后端 abnormalDeviceCount（异常设备明细条数） */
    getAbnormalDeviceCount (row) {
      if (!row) return 0;
      const raw = row.abnormalDeviceCount;
      if (raw == null || raw === '') return 0;
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
    },
    formatDate (date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('zh-CN');
    },

    // ==================== 改派相关方法 ====================

    handleReassign (task) {
      this.reassignTask = task;
      // 先回填表单再打开弹窗，便于 AssignTypeFields 根据 assignType 拉取候选人
      this.reassignForm = {
        assignType: task.assignType || 'none',
        assignUserIds: task.assignUserIds ? task.assignUserIds.split(',').filter(Boolean) : [],
        assignTeamId: task.assignTeamId || null
      };
      this.reassignVisible = true;
    },

    /** 改派弹窗内跳转人员小组管理 */
    handleReassignNavigateTeam () {
      this.reassignVisible = false;
      this.$router.push({ name: 'teamList' }).catch(() => {});
    },
    /** 改派弹窗内跳转组织人员管理 */
    handleReassignNavigateOrgUser () {
      this.reassignVisible = false;
      this.$router.push({ name: 'orgUserList' }).catch(() => {});
    },

    submitReassign () {
      const form = this.reassignForm;
      if (form.assignType === 'user' && !form.assignUserIds.length) {
        this.$message.warning('请选择至少一名执行人员');
        return;
      }
      if (form.assignType === 'team' && !form.assignTeamId) {
        this.$message.warning('请选择执行小组');
        return;
      }
      this.reassigning = true;
      taskService.reassignPeriod({
        groupId: this.groupId,
        periodName: this.reassignTask.periodName,
        assignType: form.assignType,
        assignUserIds: form.assignType === 'user' ? form.assignUserIds.join(',') : '',
        assignTeamId: form.assignType === 'team' ? form.assignTeamId : ''
      }).then(() => {
        this.$message.success('改派成功');
        this.reassignVisible = false;
        this.loadData();
      }).finally(() => {
        this.reassigning = false;
      });
    }
  },
  mounted () {
    // 从路由query取任务组信息
    this.groupId = this.$route.query.groupId;
    this.groupName = this.$route.query.groupName || '任务列表';
    this.cycleType = this.$route.query.cycleType;
    this.taskType = this.$route.query.taskType;
    this.taskYear = this.$route.query.taskYear ? this.$route.query.taskYear : null;
    this.zoneAreaId = this.$route.query.zoneAreaId || null;
    this.isSplitMonthly = this.$route.query.isSplitMonthly != null && this.$route.query.isSplitMonthly !== ''
      ? this.$route.query.isSplitMonthly
      : 'N';

    // 每日任务组默认只看未完成
    this.onlyUnfinished = this.cycleType === 'day';

    this.loadBaseData();
    this.loadData();
    // 日常周期：初始化时检查今日任务是否已存在
    if (this.cycleType === 'day') {
      this.checkTodayTask();
      this.loadDayAutoSetting();
    }
  }
};
</script>

<style scoped lang="scss">
.task-list-page {
  padding: 15px;
  background: #f5f7fa;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;

  .card {
    flex: 1;
    min-height: 0; /* flex 子项必须设置，否则无法被压缩 */
    background: #fff;
    border-radius: 4px;
    padding: 15px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    box-sizing: border-box;

    .card-header {
      flex-shrink: 0;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 15px;
      padding-bottom: 15px;
      border-bottom: 1px solid #ebeef5;

      .header-left {
        display: flex;
        align-items: center;
        gap: 4px;

        .breadcrumb { font-size: 14px; color: #909399; }
        .breadcrumb.current { color: #303133; font-weight: 600; }
        .breadcrumb-sep { font-size: 12px; color: #c0c4cc; margin: 0 4px; }
      }

      .header-right {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
      }

      .day-auto-wrap {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 0 4px;

        .day-auto-label {
          font-size: 13px;
          color: #606266;
          white-space: nowrap;
        }

        .day-auto-tip-icon {
          font-size: 15px;
          color: #909399;
          cursor: help;
        }

        .day-auto-tip-icon:hover {
          color: #409eff;
        }
      }
    }

    .search-bar {
      flex-shrink: 0;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
      padding: 10px;
      background: #f5f7fa;
      border-radius: 4px;

      .filter-switch { margin-left: 10px; }
    }

    /* 表格填充剩余高度，内容超出时表格内部滚动 */
    .table-wrapper {
      flex: 1;
      min-height: 0;
      overflow: hidden;
    }

    .progress-cell {
      display: flex;
      align-items: center;
      gap: 8px;

      .el-progress { flex: 1; }
      .progress-text { font-size: 12px; color: #909399; white-space: nowrap; }
    }

    .pagination { flex-shrink: 0; margin-top: 15px; text-align: right; }
  }
}

.danger-text { color: #f56c6c; }
.text-muted { color: #c0c4cc; }
/* 小组委派人辅助文字 */
.assign-delegate-text {
  font-size: 12px;
  color: #409eff;
  margin-top: 3px;
}

.task-info-bar {
  margin-bottom: 15px;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  font-size: 13px;

  .label { color: #909399; }
  .value { color: #303133; font-weight: 500; }
}
</style>
